import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationChoices, NotificationRule, Role } from '@/domain';
import type { NotificationFilter } from '../ports/NotificationRepository';
import { useNotificationRepository, usePreferencesRepository } from '../container';
import { keys } from '../query-keys';

/**
 * A person's notifications. Section 7.6, and the request of 7 October 2026,
 * sections 1 and 2.
 *
 * Refetched the moment the live stream says this person's bell changed
 * (useLiveUpdates), and polled once a minute besides, which is all it needs
 * when the stream is down: the bell is the one part of the shell where being
 * a minute stale is harmless.
 *
 * Every list and the count are under one key prefix, so reading one, or all,
 * refreshes the bell, the centre and the home screen together.
 */
export function useNotifications(filter: NotificationFilter = {}, enabled = true) {
  const repo = useNotificationRepository();
  return useQuery({
    queryKey: [...keys.notifications, 'list', filter],
    queryFn: () => repo.list(filter),
    staleTime: 30_000,
    refetchInterval: 60_000,
    enabled,
  });
}

/** The bell's figure, and the figure on each category's tag: counted over everything. */
export function useNotificationSummary() {
  const repo = useNotificationRepository();
  return useQuery({
    queryKey: [...keys.notifications, 'summary'],
    queryFn: () => repo.summary(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const repo = useNotificationRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repo.markRead(id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.notifications }),
  });
}

export function useMarkAllNotificationsRead() {
  const repo = useNotificationRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => repo.markAllRead(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.notifications }),
  });
}

// ------------------------------------------------------------ a person's own choices

/** The notifications the person receives, and what they chose about each. */
export function useMyNotifications(enabled = true) {
  const repo = usePreferencesRepository();
  return useQuery({
    queryKey: keys.myNotifications,
    queryFn: () => repo.myNotifications(),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useSaveMyNotifications() {
  const repo = usePreferencesRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (choices: NotificationChoices) => repo.saveMyNotifications(choices),
    onSuccess: (saved) => {
      queryClient.setQueryData(keys.myNotifications, saved);
      // What the home screen shows is one of the lists.
      void queryClient.invalidateQueries({ queryKey: keys.notifications });
    },
  });
}

// ------------------------------------------------------------ management's settings

export function useNotificationSettings(enabled = true) {
  const repo = usePreferencesRepository();
  return useQuery({
    queryKey: keys.notificationSettings,
    queryFn: () => repo.notificationSettings(),
    enabled,
  });
}

export function useSaveRoleNotifications() {
  const repo = usePreferencesRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { role: Role; rules: readonly NotificationRule[] }) =>
      repo.saveRoleNotifications(input.role, input.rules),
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.notificationSettings }),
  });
}

export function useResetRoleNotifications() {
  const repo = usePreferencesRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (role: Role) => repo.resetRoleNotifications(role),
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.notificationSettings }),
  });
}

export function useUserNotifications(userId: string | undefined) {
  const repo = usePreferencesRepository();
  return useQuery({
    queryKey: [...keys.notificationSettings, 'user', userId ?? ''],
    queryFn: () => repo.userNotifications(userId as string),
    enabled: userId !== undefined,
  });
}

export function useSaveUserNotifications() {
  const repo = usePreferencesRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; rules: readonly NotificationRule[] }) =>
      repo.saveUserNotifications(input.userId, input.rules),
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.notificationSettings }),
  });
}

export function useResetUserNotifications() {
  const repo = usePreferencesRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => repo.resetUserNotifications(userId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: keys.notificationSettings }),
  });
}
